import React from "react";
import {
  FloatingActionButton,
  Table,
  TableBody,
  TableRow,
  TableRowColumn,
  TableHeader,
  TableHeaderColumn
} from "material-ui";
import ContentAdd from "material-ui/svg-icons/content/add";
import { green500 } from "material-ui/styles/colors";
import _ from "lodash";
import autobind from "class-autobind";
import strings from "../../localization/strings";
import AppStateStore from "../../stores/AppStateStore";
import AddHierarchySettingAutoBreakItem from "./AddHierarchySettingAutoBreakItem";
import {
  IAutoBreakItem,
  EAutoBreakApplyRule,
  EAutoBreakTimeDurationApplyRule,
  IAutoBreakSettings
} from "../../models";

interface IProps {
  readonly?: boolean;
  isRoot?: boolean;
  overridden?: boolean;
  settingObj?: IAutoBreakSettings;
  handleChangeSettingObj?(settingObj: IAutoBreakSettings): void;
}

interface IState {
  openAddAutoBreakItemDialog: boolean;
  hoveredAutoBreakItemNumber: number | null;
}

export default class HierarchySettingsAutoBreaks extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      openAddAutoBreakItemDialog: false,
      hoveredAutoBreakItemNumber: null
    };
    autobind(this);
  }

  convertMinuteToHour(minutes: number) {
    const hour = Math.floor(minutes / 60);
    const extraMinutes = minutes % 60;
    if (hour === 0) {
      return extraMinutes === 1 ? `${extraMinutes} minute` : `${extraMinutes} minutes`;
    } else if (hour === 1) {
      return extraMinutes === 1
        ? `1 hour ${extraMinutes} minute`
        : `1 hour ${extraMinutes} minutes`;
    } else {
      return extraMinutes === 1
        ? `${hour} hours ${extraMinutes} minute`
        : `${hour} hours ${extraMinutes} minutes`;
    }
  }

  handleChangeValue(autoBreakItems: IAutoBreakItem[]) {
    const { settingObj } = this.props;
    if (!settingObj) return;
    settingObj.AutoBreaks = autoBreakItems;
    this.props.handleChangeSettingObj && this.props.handleChangeSettingObj(settingObj);
  }

  render() {
    const { settingObj, readonly, isRoot, overridden } = this.props;
    const autoBreakItems: IAutoBreakItem[] = _.cloneDeep(
      (settingObj && settingObj.AutoBreaks) || []
    );
    const editable = isRoot || overridden;

    return (
      <div>
        {!readonly && editable && (
          <FloatingActionButton
            data-intercom-target="menu-Hierarchy-Settings-AutoBreak-Btn-Add"
            style={styles.fab}
            mini
            secondary
            onTouchTap={() =>
              this.setState({ openAddAutoBreakItemDialog: true, hoveredAutoBreakItemNumber: null })
            }
          >
            <ContentAdd />
          </FloatingActionButton>
        )}

        {!autoBreakItems.length ? (
          strings.There_is_no_Auto_Break_item
        ) : (
          <Table
            multiSelectable={false}
            selectable={false}
            fixedHeader={true}
            style={styles.table}
            onRowHover={rowNumber => this.setState({ hoveredAutoBreakItemNumber: rowNumber })}
          >
            <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
              <TableRow>
                <TableHeaderColumn style={styles.colSmall}>{strings.Name}</TableHeaderColumn>
                <TableHeaderColumn style={styles.colSmall}>
                  {strings.Break_Duration}
                </TableHeaderColumn>
                <TableHeaderColumn style={styles.colSmall}>
                  {strings.Trigger_Duration}
                </TableHeaderColumn>
                <TableHeaderColumn style={styles.colSmall}>{strings.Rule}</TableHeaderColumn>
                <TableHeaderColumn style={styles.colMedium}>
                  {strings.Time_Duration_Rule}
                </TableHeaderColumn>
                <TableHeaderColumn style={styles.colSmall}>
                  {strings.Apply_Once_Per_PunchSet}
                </TableHeaderColumn>
                <TableHeaderColumn style={styles.colLarge}>
                  {strings.Apply_Once_Per_Day_to_the_1st_Qualified_PunchSet}
                </TableHeaderColumn>
                <TableHeaderColumn style={styles.colLarge}>
                  {strings.Apply_Once_Per_Day_based_on_Total_Working_Hours}
                </TableHeaderColumn>
                {!readonly && editable && <TableHeaderColumn style={styles.colTiny} />}
              </TableRow>
            </TableHeader>

            <TableBody
              showRowHover={editable}
              displayRowCheckbox={false}
              deselectOnClickaway={false}
            >
              {autoBreakItems.map((row, index) => (
                <TableRow
                  data-intercom-target={
                    index === 0 && "menu-Hierarchy-Settings-AutoBreakRow-openItemDetails"
                  }
                  key={`row-${index}`}
                >
                  <TableRowColumn style={styles.colSmall}>{row.BreakName}</TableRowColumn>
                  <TableRowColumn style={styles.colSmall}>
                    {this.convertMinuteToHour(row.BreakDurationInMinutes)}
                  </TableRowColumn>
                  <TableRowColumn style={styles.colSmall}>
                    {this.convertMinuteToHour(row.BreakTriggerDurationInMinutes)}
                  </TableRowColumn>
                  <TableRowColumn style={styles.colSmall}>
                    {row.ApplyRule === EAutoBreakApplyRule.PunchHierarchy
                      ? strings.Based_On_Punch
                      : strings.Based_On_Employee_Default_Hierarchy}
                  </TableRowColumn>
                  <TableRowColumn style={styles.colMedium}>
                    {row.TimeDurationRule === EAutoBreakTimeDurationApplyRule.ActualTime
                      ? strings.Based_On_Actual_Time
                      : strings.Based_On_Rounded_Time}
                  </TableRowColumn>
                  <TableRowColumn style={{ ...styles.colSmall, textAlign: "center" }}>
                    {row.OneTimeApply && (
                      <i className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
                    )}
                  </TableRowColumn>
                  <TableRowColumn style={{ ...styles.colLarge, textAlign: "center" }}>
                    {row.OneTimeApplyPerDay && (
                      <i className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
                    )}
                  </TableRowColumn>
                  <TableRowColumn style={{ ...styles.colLarge, textAlign: "center" }}>
                    {row.OneTimeApplyPerDayByTotalHours && (
                      <i className="fa fa-check" style={{ fontSize: 18, color: green500 }} />
                    )}
                  </TableRowColumn>
                  {!readonly && editable && (
                    <TableRowColumn style={styles.colTiny} className="prevent-cell-click">
                      {this.state.hoveredAutoBreakItemNumber !== null &&
                        this.state.hoveredAutoBreakItemNumber === index && (
                          <div onClick={e => e.stopPropagation()}>
                            <i
                              data-intercom-target={
                                index === 0 &&
                                "menu-Hierarchy-Settings-AutoBreakRow-Btn-OpenEditItemDialog"
                              }
                              className="fa fa-pencil"
                              style={styles.actionIcon}
                              onClick={() =>
                                this.setState({
                                  openAddAutoBreakItemDialog: true
                                })
                              }
                            />
                            <i
                              data-intercom-target={
                                index === 0 &&
                                "menu-Hierarchy-Settings-AutoBreakRow-Btn-OpenRemoveItemDialog"
                              }
                              className="fa fa-trash-o"
                              style={{ ...styles.actionIcon, marginLeft: 10 }}
                              onClick={() =>
                                AppStateStore.setConfirmDialogContent({
                                  content:
                                    strings.Are_you_sure_you_want_to_delete_the_selected_auto_break,
                                  confirm: () => {
                                    autoBreakItems.splice(
                                      this.state.hoveredAutoBreakItemNumber as number,
                                      1
                                    );
                                    this.handleChangeValue(autoBreakItems);
                                  }
                                })
                              }
                            />
                          </div>
                        )}
                    </TableRowColumn>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {this.state.openAddAutoBreakItemDialog && (
          <AddHierarchySettingAutoBreakItem
            updateAutoBreakItem={
              this.state.hoveredAutoBreakItemNumber !== null
                ? autoBreakItems[this.state.hoveredAutoBreakItemNumber]
                : undefined
            }
            handleClose={() =>
              this.setState({ openAddAutoBreakItemDialog: false, hoveredAutoBreakItemNumber: null })
            }
            handleAdd={autoBreakItem => {
              this.setState({
                openAddAutoBreakItemDialog: false,
                hoveredAutoBreakItemNumber: null
              });
              autoBreakItems.push(autoBreakItem);
              this.handleChangeValue(autoBreakItems);
            }}
            handleUpdate={autoBreakItem => {
              if (this.state.hoveredAutoBreakItemNumber !== null) {
                autoBreakItems[this.state.hoveredAutoBreakItemNumber] = autoBreakItem;
              }
              this.setState({
                openAddAutoBreakItemDialog: false,
                hoveredAutoBreakItemNumber: null
              });
              this.handleChangeValue(autoBreakItems);
            }}
          />
        )}
      </div>
    );
  }
}

const styles = {
  fab: {
    position: "absolute",
    right: 42,
    display: "inline-block",
    zIndex: 100,
    top: -20
  },
  table: {
    tableLayout: "fixed",
    width: "100%"
  },
  // We have 9 columns below, so let's choose percentages so they sum up near 100%.
  // Adjust them according to which columns you want wider or narrower:
  colTiny: {
    width: "5%", // pencil/trash
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingLeft: 2,
    paddingRight: 2
  },
  colSmall: {
    width: "10%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingLeft: 2,
    paddingRight: 2
  },
  colMedium: {
    width: "15%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingLeft: 2,
    paddingRight: 2
  },
  colLarge: {
    width: "20%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingLeft: 2,
    paddingRight: 2
  },
  actionIcon: {
    fontSize: 20,
    fontWeight: "bold" as "bold",
    cursor: "pointer"
  }
};
